import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  FormHelperText,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import * as React from "react";
import { DIALOG_WIDTH } from "../../constants";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { useDeleteAlertSchema } from "../../hooks/mutation/update/useDeleteAlertSchema";
import { useDeleteDashboard } from "../../hooks/mutation/update/useDeleteDashboard";
import { useDeleteIntegration } from "../../hooks/mutation/update/useDeleteIntegration";
import { useDeleteAlert } from "../../hooks/mutation/update/useDeleteAlert";
import { useDeleteService } from "../../hooks/mutation/update/useDeleteService";
import { useDeleteKpi } from "../../hooks/mutation/update/useDeleteKpi";
import { useDeleteServiceTemplate } from "../../hooks/mutation/update/useDeleteServiceTemplate";
import { useDeleteUser } from "../../hooks/mutation/update/useDeleteUser";
import { useIntegrations } from "../../hooks/query/useIntegrations";
import { useAlerts } from "../../hooks/query/useAlerts";
import { useServiceTemplates } from "../../hooks/query/useServiceTemplates";
import { useKpis } from "../../hooks/query/useKpis";
import { useAlertProviders } from "../../hooks/query/useAlertProviders";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

export type DeleteEntity =
  | "dashboard"
  | "alert"
  | "alert-schema"
  | "integration"
  | "service"
  | "kpi"
  | "service-template"
  | "user";

const ENTITY_LABELS: Record<
  DeleteEntity,
  { singular: string; plural: string }
> = {
  dashboard: { singular: "Control Plane", plural: "Control Planes" },
  alert: { singular: "alert", plural: "alerts" },
  "alert-schema": { singular: "alert schema", plural: "alert schemas" },
  integration: { singular: "integration", plural: "integrations" },
  service: { singular: "business service", plural: "business services" },
  kpi: { singular: "KPI", plural: "KPIs" },
  "service-template": {
    singular: "service template",
    plural: "service templates",
  },
  user: { singular: "user", plural: "users" },
};

const parseIds = (raw: string): string[] => {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

const ConfirmDeleteDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  entity: DeleteEntity;
  ids: string;
  serviceMapId?: string;
}> = ({ onDialogClosed, entity, ids, serviceMapId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const { mutateAsync: deleteDashboard } = useDeleteDashboard();
  const { mutateAsync: deleteAlertSchema } = useDeleteAlertSchema();
  const { mutateAsync: deleteIntegration } = useDeleteIntegration();
  const { mutateAsync: deleteAlert } = useDeleteAlert();
  const { mutateAsync: deleteService } = useDeleteService();
  const { mutateAsync: deleteKpi } = useDeleteKpi();
  const { mutateAsync: deleteServiceTemplate } = useDeleteServiceTemplate();
  const { mutateAsync: deleteUser } = useDeleteUser();
  const { data: integrations = [] } = useIntegrations();
  const { data: alerts = [] } = useAlerts();
  const { data: serviceTemplates = [] } = useServiceTemplates();
  const { data: kpis = [] } = useKpis();
  const { data: alertProviders = [] } = useAlertProviders();

  const allIds = React.useMemo(() => parseIds(ids), [ids]);

  const deletableIds = React.useMemo(() => {
    switch (entity) {
      case "alert-schema":
        return allIds.filter((id) => {
          const schema = alertProviders.find((row) => row.id === id);
          return schema ? schema.system !== true : true;
        });
      case "integration":
        return allIds.filter((id) => {
          const integration = integrations.find((row) => row.id === id);
          return integration ? integration.system !== true : true;
        });
      case "kpi":
        return allIds.filter((id) => {
          const kpi = kpis.find((row) => row.id === id);
          return kpi ? kpi.system !== true : true;
        });
      case "service-template":
        return allIds.filter((id) => {
          const template = serviceTemplates.find((row) => row.id === id);
          return template ? !template.system : true;
        });
      default:
        return allIds;
    }
  }, [alertProviders, allIds, entity, integrations, kpis, serviceTemplates]);

  const skippedIds = React.useMemo(() => {
    switch (entity) {
      case "alert-schema":
        return allIds.filter((id) => {
          const schema = alertProviders.find((row) => row.id === id);
          return schema ? schema.system === true : false;
        });
      case "integration":
        return allIds.filter((id) => {
          const integration = integrations.find((row) => row.id === id);
          return integration ? integration.system === true : false;
        });
      case "kpi":
        return allIds.filter((id) => {
          const kpi = kpis.find((row) => row.id === id);
          return kpi ? kpi.system === true : false;
        });
      case "service-template":
        return allIds.filter((id) => {
          const template = serviceTemplates.find((row) => row.id === id);
          return template ? template.system : false;
        });
      default:
        return [];
    }
  }, [alertProviders, allIds, entity, integrations, kpis, serviceTemplates]);

  const count = deletableIds.length;
  const labels = ENTITY_LABELS[entity];
  const associatedIntegrationIds = React.useMemo(() => {
    if (entity !== "alert-schema") {
      return [];
    }

    if (deletableIds.length === 0) {
      return [];
    }

    const schemaIds = new Set(deletableIds);
    const integrationIds = new Set<string>();

    for (const integration of integrations) {
      if (schemaIds.has(integration.alertSchemaId)) {
        integrationIds.add(integration.id);
      }
    }

    return Array.from(integrationIds);
  }, [deletableIds, entity, integrations]);

  const associatedAlertIds = React.useMemo(() => {
    if (entity !== "alert-schema") {
      return [];
    }

    const alertIds = new Set<string>();
    for (const alert of alerts) {
      if (associatedIntegrationIds.includes(alert.integrationId)) {
        alertIds.add(alert.id);
      }
    }

    return Array.from(alertIds);
  }, [alerts, associatedIntegrationIds, entity]);

  const onClose = () => {
    if (isDeleting) {return;}
    setSubmitError(null);
    onDialogClosed();
  };

  const onConfirm = async () => {
    if (isDeleting) {return;}

    if (count === 0) {
      onDialogClosed({
        message: "No deletable items selected.",
        severity: "info",
      });
      return;
    }

    try {
      setSubmitError(null);
      setIsDeleting(true);

      switch (entity) {
        case "dashboard":
          for (const dashboardId of deletableIds) {
            await deleteDashboard({ dashboardId });
          }
          break;

        case "alert-schema":
          for (const alertId of associatedAlertIds) {
            await deleteAlert({ alertId });
          }

          for (const integrationId of associatedIntegrationIds) {
            await deleteIntegration({ integrationId });
          }

          for (const alertSchemaId of deletableIds) {
            await deleteAlertSchema({ alertSchemaId });
          }
          break;

        case "alert":
          for (const alertId of deletableIds) {
            await deleteAlert({ alertId });
          }
          break;

        case "integration":
          for (const integrationId of deletableIds) {
            await deleteIntegration({ integrationId });
          }
          break;

        case "service":
          if (!serviceMapId) {
            throw new Error("Service map id is required to delete a service.");
          }

          for (const serviceId of deletableIds) {
            await deleteService({ serviceMapId, serviceId });
          }
          break;
        case "kpi":
          for (const kpiId of deletableIds) {
            await deleteKpi({ kpiId });
          }
          break;
        case "service-template":
          for (const serviceTemplateId of deletableIds) {
            await deleteServiceTemplate({ serviceTemplateId });
          }
          break;
        case "user":
          for (const userId of deletableIds) {
            await deleteUser({ userId });
          }
          break;
      }

      let msg =
        count === 1
          ? `Deleted 1 ${labels.singular}.`
          : `Deleted ${count} ${labels.plural}.`;

      if (entity === "alert-schema") {
        const details: string[] = [];
        if (associatedIntegrationIds.length > 0) {
          details.push(
            `${associatedIntegrationIds.length} associated ${
              associatedIntegrationIds.length === 1 ? "integration" : "integrations"
            }`,
          );
        }
        if (associatedAlertIds.length > 0) {
          details.push(
            `${associatedAlertIds.length} associated ${
              associatedAlertIds.length === 1 ? "alert" : "alerts"
            }`,
          );
        }

        if (details.length > 0) {
          if (count === 1) {
            msg = `Deleted 1 ${labels.singular}, ${details.join(" and ")}.`;
          } else {
            msg = `Deleted ${count} ${labels.plural}, ${details.join(" and ")}.`;
          }
        }
      }

      onDialogClosed({ message: msg, severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const title = count === 1 ? `Delete ${labels.singular}` : `Delete ${labels.plural}`;

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle title={title} isSmallScreen={isSmallScreen} onClose={onClose} />

      <Box component="form" onSubmit={(e) => {
        e.preventDefault();
        void onConfirm();
      }}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ display: "grid", placeItems: "center" }}>
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Typography variant="body2" color="text.secondary">
                {count === 1
                  ? `Are you sure you want to delete this ${labels.singular}?`
                  : `Are you sure you want to delete these ${count} ${labels.plural}?`}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
              </Typography>

              {entity === "alert-schema" && associatedIntegrationIds.length > 0 && (
                <Typography variant="body2" color="warning.main">
                  {associatedIntegrationIds.length === 1
                    ? "1 associated tool integration will also be deleted."
                    : `${associatedIntegrationIds.length} associated tool integrations will also be deleted.`}
                </Typography>
              )}

              {entity === "alert-schema" && associatedAlertIds.length > 0 && (
                <Typography variant="body2" color="warning.main">
                  {associatedAlertIds.length === 1
                    ? "1 associated alert will also be deleted."
                    : `${associatedAlertIds.length} associated alerts will also be deleted.`}
                </Typography>
              )}

              {skippedIds.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  {entity === "alert-schema" &&
                    (skippedIds.length === 1
                      ? "1 system alert schema cannot be deleted and will be kept."
                      : `${skippedIds.length} system alert schemas cannot be deleted and will be kept.`)}
                  {entity === "integration" &&
                    (skippedIds.length === 1
                      ? "1 system integration cannot be deleted and will be kept."
                      : `${skippedIds.length} system integrations cannot be deleted and will be kept.`)}
                  {entity === "kpi" &&
                    (skippedIds.length === 1
                      ? "1 system KPI cannot be deleted and will be kept."
                      : `${skippedIds.length} system KPIs cannot be deleted and will be kept.`)}
                  {entity === "service-template" &&
                    (skippedIds.length === 1
                      ? "1 system service template cannot be deleted and will be kept."
                      : `${skippedIds.length} system service templates cannot be deleted and will be kept.`)}
                </Typography>
              )}

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Delete"}
          primaryIcon={<></>}
          primaryColor="error"
          onCancel={onClose}
          loading={isDeleting}
          disabled={isDeleting}
        />
      </Box>
    </Dialog>
  );
};

export default ConfirmDeleteDialog;
